import { useQuery } from '@tanstack/react-query';
import apiService, {
  type ConsumerUser,
  type ConsumerFilterBy,
} from '@/services/apiService';

export interface UseConsumerUsersResult {
  users: ConsumerUser[];
  isLoading: boolean;
  isRefetching: boolean;
  error: string | null;
}

/**
 * Fetches the list of consumers for a given account using the v2 endpoint.
 *
 * Uses React Query so that StrictMode's double-mount does not issue two
 * network requests — React Query deduplicates in-flight requests for the
 * same query key automatically.
 *
 * - Pass `filterBy` to apply server-side role / location filtering.
 * - Omit `filterBy` (or pass `undefined`) to fetch all consumers with no filter.
 * - Re-fetches automatically whenever `consumerAccountId` or `filterBy` values change.
 *
 * @param consumerAccountId - The consumer account to query. Fetch is skipped when null.
 * @param filterBy          - Optional server-side filter (roles + locationIds as comma-separated strings).
 */
export function useConsumerUsers(
  consumerAccountId: string | null,
  filterBy?: ConsumerFilterBy,
): UseConsumerUsersResult {
  // Stable cache key: include both consumerAccountId and the serialised filter.
  const filterKey = filterBy ? JSON.stringify(filterBy) : '';

  const { data, isLoading, isFetching, error } = useQuery<ConsumerUser[], Error>({
    queryKey: ['consumerUsers', consumerAccountId, filterKey],
    queryFn: () => apiService.getConsumerUsersV2(consumerAccountId!, filterBy),
    enabled: !!consumerAccountId,
    // Keep previous data visible while a new filter is loading.
    placeholderData: (prev) => prev,
    // Employees list does not need background refetches on window focus.
    refetchOnWindowFocus: false,
  });

  return {
    users: data ?? [],
    isLoading,
    isRefetching: isFetching && !isLoading,
    error: error ? 'Unable to load employees. Please try again later.' : null,
  };
}
